import { Link, useNavigate } from 'react-router-dom';
import { useSettings } from '../../app/settings';
import { isDriver, useSession } from '../../app/session';
import { toast } from '../../app/toast';
import { ChevronIcon } from '../../components/Icons';
import { LanguageSelect } from '../../components/LanguageSelect';
import { PageHeader } from '../../components/PageHeader';
import { APP_VERSION, CRM_URL } from '../../config';
import { useT, type DictKey } from '../../i18n';

/** «Ще»: language, geolocation toggle, password, vehicles, CRM link, version, logout. */
export function SettingsPage() {
  const t = useT();
  const nav = useNavigate();
  const me = useSession((s) => s.me);
  const logout = useSession((s) => s.logout);
  const geo = useSettings((s) => s.geo);
  const setGeo = useSettings((s) => s.setGeo);

  function toggleGeo(on: boolean) {
    if (!on) return setGeo(false);
    const g = globalThis.navigator?.geolocation;
    if (!g) {
      toast(t('settings.geoDenied'), 'bad');
      return;
    }
    // ask once now, so the permission prompt does not appear in the middle of a step
    g.getCurrentPosition(
      () => setGeo(true),
      () => {
        setGeo(false);
        toast(t('settings.geoDenied'), 'bad');
      },
      { timeout: 10000 },
    );
  }

  return (
    <>
      <PageHeader title={t('more.title')} />
      {me ? (
        <div className="card">
          <p className="text-[14px] text-lab">{t('settings.account')}</p>
          <p className="text-[17px] font-semibold">{me.user.full_name || me.user.email}</p>
          <p className="text-[15px] text-txt">
            {me.user.email} · {t(`role.${me.role}` as DictKey)}
          </p>
          {me.organization?.name ? <p className="text-[15px] text-txt">{me.organization.name}</p> : null}
        </div>
      ) : null}

      <div className="mt-4 space-y-4">
        <LanguageSelect id="settings-lang" />

        <label className="card flex min-h-tap items-center gap-3">
          <span className="min-w-0 flex-1">
            <span className="block font-medium">{t('settings.geo')}</span>
            <span className="block text-[14px] text-lab">{t('settings.geoHint')}</span>
          </span>
          <input type="checkbox" role="switch" className="h-7 w-7 flex-none" checked={geo} onChange={(e) => toggleGeo(e.target.checked)} />
        </label>

        <nav className="card divide-y divide-line py-0">
          <Link to="/vehicles" className="tap-row py-1">
            <span className="flex-1">{t('trips.vehicles')}</span>
            <ChevronIcon />
          </Link>
          <Link to="/password" className="tap-row py-1">
            <span className="flex-1">{t('settings.password')}</span>
            <ChevronIcon />
          </Link>
          {/* the CRM is the office's tool: a driver works only here (and needs a separate sign-in there anyway) */}
          {CRM_URL && !isDriver(me) ? (
            <a href={CRM_URL} target="_blank" rel="noreferrer" className="tap-row py-1">
              <span className="flex-1">
                <span className="block">{t('settings.crm')}</span>
                <span className="block text-[13px] text-lab">{t('settings.crmHint')}</span>
              </span>
              <ChevronIcon />
            </a>
          ) : null}
        </nav>

        <button type="button" className="btn-danger w-full" onClick={() => void logout().then(() => nav('/login', { replace: true }))}>
          {t('settings.logout')}
        </button>

        <p className="text-center text-[13px] text-lab">
          {t('settings.version')}: {APP_VERSION}
        </p>
      </div>
    </>
  );
}
